import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, resolveDynamicComponent as _resolveDynamicComponent, Transition as _Transition, withCtx as _withCtx, resolveComponent as _resolveComponent, createElementVNode as _createElementVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { useRouter } from "vue-router";
import { computed, nextTick, onBeforeMount, onMounted, ref } from "vue";
import { useToast } from "vue-toastification";
import { Quiz } from "@/models/Quiz";
import { QuizQuestion } from "@/models/QuizQuestion";
import { QuizAnswer } from "@/models/QuizAnswer";
import { progress } from "./composables/progress";
import config from "@/config.json";
import quizData from "@/assets/quiz/data.json";
import HeaderComponent from "@/components/HeaderComponent.vue";
import LoadingComponent from "@/components/LoadingComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const router = useRouter();
const quizzes = ref<Quiz[]>(quizData as Array<Quiz>);
const isLoading = ref(true);
const overlayIsVisible = ref(false);
const hasProgress = computed(() => {
  const hasCompletedQuizzes = quizzes.value.filter(
    (quiz: Quiz) => quiz.completed === true
  );

  const hasQuizProgress = quizzes.value.filter((quiz: Quiz) => {
    return quiz.questions?.some((question: QuizQuestion) => {
      return question.answers?.some((answer: QuizAnswer) => {
        return answer.selected === true;
      });
    });
  });

  return hasCompletedQuizzes.length > 0 || hasQuizProgress.length > 0;
});

onBeforeMount(() => {
  const toast = useToast();

  // Set quiz question & answer ids
  quizzes.value.forEach((quiz) => {
    quiz.questions?.forEach((question, questionIndex) => {
      question['id'] = questionIndex;

      question.answers?.forEach((answer, answerIndex) => {
        answer['id'] = answerIndex;
      });
    });
  });

  // Sort quizzes
  quizzes.value.sort((a, b) =>
    (typeof a.comingSoon !== "undefined" && a.comingSoon) ===
    (typeof b.comingSoon !== "undefined" && b.comingSoon)
      ? 0
      : typeof a.comingSoon !== "undefined" && a.comingSoon
      ? 1
      : -1
  );

  // Preload images
  quizzes.value.forEach((quiz) => {
    quiz.questions?.forEach((question) => {
      quiz.completed = false;
      if (!question.image) {
        return;
      }

      const image = new Image();
      image.src = require(`@/assets/quiz/images/${quiz.id}/${question.image}`);
    });
  });

  // Get quizzes from local storage
  const quizzesInLocalStorage = localStorage.getItem('dive-quizzes');
  const _quizzes = quizzesInLocalStorage && JSON.parse(quizzesInLocalStorage);

  // Version unchanged, set quizzes from local storage
  if (_quizzes !== null && _quizzes.version === config.version) {
    // Single quiz version has changed
    let toastTimer = 0;
    const updated = _quizzes.quizzes.map((_quiz: Quiz, quizIndex: number) => {
      if (_quiz.version !== null && _quiz.version !== quizzes.value[quizIndex].version && !quizzes.value[quizIndex].comingSoon) {
        toastTimer++;

        setTimeout(() => { 
          nextTick(() => {
          toast.success(`Wir haben das Quiz "${quizzes.value[quizIndex].name}" aktualisiert. Dein Fortschritt wurde zurückgesetzt.`, {
            id: `quiz-${quizzes.value[quizIndex].id}-version-changed`,
          });
        });
        }, toastTimer * 1000);

        return quizzes.value[quizIndex];
      }

      // Single quiz version hasn't changed
      return {
        ..._quiz,
        name: quizzes.value[quizIndex].name,
        description: quizzes.value[quizIndex].description,
        questions: _quiz.questions.map((question: QuizQuestion, questionIndex: number) => {
          return {
            ...question,
            text: quizzes.value[quizIndex].questions[questionIndex].text,
            explanation: quizzes.value[quizIndex].questions[questionIndex].explanation || null,
            answers: question.answers.map((answer: QuizAnswer) => {
              const index = quizzes.value[quizIndex].questions[questionIndex].answers.findIndex((a) => {
                return a.id === answer.id;
              });
              
              return {
                ... quizzes.value[quizIndex].questions[questionIndex].answers[index],
                selected: answer.selected
              };
            }),
          };
        }),
      };
    });

    quizzes.value = updated;
  } else if (_quizzes !== null && _quizzes.version !== config.version) { // App version changed
    nextTick(() => {
      toast.success("Wir haben die Quiz-App aktualisiert. Dein Fortschritt wurde zurückgesetzt.", {
        id: "app-version-changed",
      });
    });
  }

  persistInLocalStorage();
});

onMounted(async () => {
  setIsLoading(false);

  // Prevent document scroll on keydown "space"
  window.addEventListener("keydown", (e) => {
    if (
      (e.key === " " || e.key === "Spacebar") &&
      (e.target as HTMLElement)?.tagName !== "INPUT"
    ) {
      e.preventDefault();
    }
  });
});

const getQuiz = (id: string) => {
  return quizzes.value?.find((quiz) => quiz.id === id);
};

const setQuiz = (event: string) => {
  const quiz = getQuiz(event);

  if (quiz && !quiz.comingSoon) {
    router.push({
      name: "quiz",
      params: {
        id: quiz.id,
        questionIndex: 0,
      },
    });
  }
};

const selectAnswer = (event: {
  quizId: string;
  questionIndex: number;
  answerIndex: number;
}) => {
  const quiz = getQuiz(event.quizId);

  if (!quiz) {
    return;
  }

  const answer = quiz.questions[event.questionIndex].answers[event.answerIndex];

  if (answer?.selected) {
    quiz.questions[event.questionIndex].answers[event.answerIndex].selected =
      false;
  } else {
    if (isSingleChoice(quiz.questions[event.questionIndex])) {
      quiz.questions[event.questionIndex].answers.forEach(
        (answer: QuizAnswer) => (answer.selected = false)
      );
    }

    answer.selected = true;
  }

  quiz.progress = progress(quiz);

  updateScore(quiz);
};

const isSingleChoice = (question: QuizQuestion) => {
  const correctAnswers = question.answers?.filter((answer) => answer.correct);

  return correctAnswers.length === 1;
};

const updateScore = (quiz: Quiz) => {
  let score = 0;

  quiz.questions?.forEach((question) => {
    let questionScoreInPercent;

    let correctAnswers = 0;
    let correctAnswersSelected = 0;

    question.answers?.forEach((answer) => {
      if (answer.selected && answer.correct) {
        correctAnswers++;
        correctAnswersSelected++;
      } else if (answer.selected && !answer.correct) {
        correctAnswersSelected -= 0.4;
      } else if (!answer.selected && answer.correct) {
        correctAnswers++;
        correctAnswersSelected--;
      }
    });

    correctAnswersSelected =
      correctAnswersSelected < 0 ? 0 : correctAnswersSelected;

    questionScoreInPercent = (100 / correctAnswers) * correctAnswersSelected;
    score += questionScoreInPercent;
  });

  const _quiz = getQuiz(quiz.id);

  if (_quiz) {
    _quiz.score =
      quiz.questions?.length > 0
        ? (score / quiz.questions.length / 10) * 10
        : 0;

    persistInLocalStorage();
  }
};

const persistInLocalStorage = () => {
  const quizzesObj = {
    version: config.version,
    quizzes: quizzes.value,
  };

  localStorage.setItem("dive-quizzes", JSON.stringify(quizzesObj));
};

const reset = () => {
  setIsLoading(true);

  quizzes.value.forEach((quiz) => {
    quiz.completed = false;

    quiz.questions?.forEach((question) => {
      question.answers?.forEach((answer) => {
        answer.selected = false;
      });
    });
  });

  quizzes.value.forEach((quiz) => updateScore(quiz));

  localStorage.removeItem("dive-quizzes");

  setIsLoading(false);

  const toast = useToast();

  toast.success("Dein Quiz-Fortschritt wurde zurückgesetzt.", {
    id: "well-done",
  });
};

const setIsLoading = (event: boolean) => {
  if (event) {
    isLoading.value = true;
    return;
  }

  setTimeout(() => {
    isLoading.value = false;
  }, 500);
};

const toggleOverlay = (event: boolean) => {
  overlayIsVisible.value = event;
};

return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (isLoading.value)
      ? (_openBlock(), _createBlock(LoadingComponent, { key: 0 }))
      : _createCommentVNode("", true),
    _createVNode(HeaderComponent),
    _createElementVNode("main", null, [
      _createVNode(_component_router_view, {
        quizzes: quizzes.value,
        overlayIsVisible: overlayIsVisible.value,
        hasProgress: hasProgress.value,
        onReset: _cache[0] || (_cache[0] = ($event: any) => (reset())),
        onSetQuiz: _cache[1] || (_cache[1] = ($event: any) => (setQuiz($event))),
        onUpdateScore: _cache[2] || (_cache[2] = ($event: any) => (updateScore($event))),
        onSelectAnswer: _cache[3] || (_cache[3] = ($event: any) => (selectAnswer($event))),
        onSetIsLoading: _cache[4] || (_cache[4] = ($event: any) => (setIsLoading($event))),
        onToggleOverlay: _cache[5] || (_cache[5] = ($event: any) => (toggleOverlay($event)))
      }, {
        default: _withCtx(({ Component }) => [
          _createVNode(_Transition, {
            name: "fade",
            mode: "out-in"
          }, {
            default: _withCtx(() => [
              (_openBlock(), _createBlock(_resolveDynamicComponent(Component)))
            ]),
            _: 2
          }, 1024)
        ]),
        _: 1
      }, 8, ["quizzes", "overlayIsVisible", "hasProgress"]),
      _createVNode(FooterComponent)
    ])
  ], 64))
}
}

})